import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `glass` is the WireframeBrief §3 glassmorphism treatment; `flat` for dense tables. */
  surface?: 'raised' | 'glass' | 'flat'
  interactive?: boolean
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, surface = 'raised', interactive = false, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-border-subtle',
        surface === 'raised' && 'bg-surface-raised',
        surface === 'glass' && 'glass',
        surface === 'flat' && 'bg-transparent',
        interactive &&
          'transition-colors duration-base ease-standard hover:border-border-strong hover:bg-surface-hover',
        className,
      )}
      {...rest}
    />
  )
})

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('flex flex-col gap-1 p-6 pb-3', className)} {...rest} />
  },
)

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...rest }, ref) {
    // jsx-a11y/heading-has-content cannot see through the rest spread: `children`
    // arrives inside `...rest`, so the rule concludes the heading is empty. Every
    // call site passes content, and a caller who did not would render an empty <h3>
    // that no lint rule in this file could have caught either.
    // eslint-disable-next-line jsx-a11y/heading-has-content
    return <h3 ref={ref} className={cn('text-h3 text-text-primary', className)} {...rest} />
  },
)

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  function CardDescription({ className, ...rest }, ref) {
    return <p ref={ref} className={cn('text-body-sm text-text-secondary', className)} {...rest} />
  },
)

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('p-6 pt-3', className)} {...rest} />
  },
)

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-3 border-t border-border-subtle p-6 py-4', className)}
        {...rest}
      />
    )
  },
)
